import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path: string) => location.pathname === path;

  const handleCinewallOpMaatClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const section = document.getElementById('cinewall-op-maat');
        if (section) {
          section.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } else {
      const section = document.getElementById('cinewall-op-maat');
      if (section) {
        section.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { path: '/', label: 'Home' },
    { path: '/#cinewall-op-maat', label: 'Cinewall op maat', isAnchor: true },
    { path: '/cinewall-met-haard', label: 'Cinewall met haard' },
    { path: '/cinewall-prijs', label: 'Prijzen' },
    { path: '/cinewall-inspiratie', label: 'Inspiratie' },
    { path: '/cinewall-showroom', label: 'Showroom' },
  ];

  return (
    <>
      {/* DESKTOP HEADER WRAPPER - Two rows stacked */}
      <div className="hidden md:block fixed top-0 left-0 right-0 z-50">
        {/* Row 1: Top Contact Bar - 36px */}
        <div className="bg-[#0F0F0F] text-[#F3F1ED] border-b border-[#2A2A2A] h-[36px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full">
            <div className="flex items-center justify-between h-full text-xs">
              <div className="flex items-center gap-6">
                <a href="tel:+31624172293" className="flex items-center gap-2 hover:text-[#C7A86A] transition-colors whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  <span>+31(0) 62 417 22 93</span>
                </a>
                <a href="tel:+31307810110" className="flex items-center gap-2 hover:text-[#C7A86A] transition-colors whitespace-nowrap">
                  <i className="ri-phone-line w-4 h-4 flex items-center justify-center"></i>
                  <span>+31(0) 30 781 01 10</span>
                </a>
                <a href="mailto:info@cinewallkopen.nl" className="flex items-center gap-2 hover:text-[#C7A86A] transition-colors whitespace-nowrap">
                  <i className="ri-mail-line w-4 h-4 flex items-center justify-center"></i>
                  <span>info@cinewallkopen.nl</span>
                </a>
              </div>
              <div className="flex items-center gap-2 text-[#BFBFBF]">
                <i className="ri-time-line w-4 h-4 flex items-center justify-center"></i>
                <span className="whitespace-nowrap">Ma-Vr: 10:00-17:00 | Za-Zo: 10:00-18:00</span>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Main Navigation - 72px */}
        <nav
          className={`h-[72px] transition-colors duration-300 ${
            isScrolled ? 'bg-white shadow-md' : 'bg-transparent'
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12 h-full">
            <div className="flex items-center justify-between h-full">
              {/* Logo */}
              <Link to="/" className="flex-shrink-0">
                <div className="bg-white px-5 py-3 rounded-md shadow-md">
                  <div className="flex flex-col items-center">
                    <span className="text-[#0F0F0F] font-bold text-xl leading-tight tracking-tight">CINEWALL</span>
                    <span className="text-[#C7A86A] font-semibold text-xs leading-tight tracking-wide">kopen</span>
                  </div>
                </div>
              </Link>

              {/* Navigation Items */}
              <div className="flex items-center gap-8">
                {navItems.map((item) => (
                  item.isAnchor ? (
                    <a
                      key={item.path}
                      href={item.path}
                      onClick={handleCinewallOpMaatClick}
                      className="text-sm transition-colors duration-300 text-[#0F0F0F] hover:text-[#C7A86A] cursor-pointer whitespace-nowrap"
                    >
                      {item.label}
                    </a>
                  ) : (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`text-sm transition-colors duration-300 relative whitespace-nowrap ${
                        isActive(item.path)
                          ? 'text-[#0F0F0F] font-semibold'
                          : 'text-[#0F0F0F] hover:text-[#C7A86A]'
                      }`}
                    >
                      {item.label}
                      {isActive(item.path) && (
                        <span className="absolute -bottom-1 left-0 right-0 h-[1px] bg-[#C7A86A]"></span>
                      )}
                    </Link>
                  )
                ))}
                <Link
                  to="/cinewall-configurator"
                  className="bg-[#C7A86A] text-[#0F0F0F] px-6 py-2.5 rounded-md font-semibold text-sm hover:bg-[#B89858] transition-colors duration-300 whitespace-nowrap"
                >
                  Offerte aanvragen
                </Link>
              </div>
            </div>
          </div>
        </nav>
      </div>

      {/* MOBILE HEADER - Single row only */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white shadow-md h-[64px]">
        <div className="w-full px-6 h-full">
          <div className="flex items-center justify-between h-full">
            {/* Logo - Centered */}
            <Link to="/" className="absolute left-1/2 -translate-x-1/2">
              <div className="bg-white px-4 py-2 rounded-md shadow-md">
                <div className="flex flex-col items-center">
                  <span className="text-[#0F0F0F] font-bold text-lg leading-tight tracking-tight">CINEWALL</span>
                  <span className="text-[#C7A86A] font-semibold text-[10px] leading-tight tracking-wide">kopen</span>
                </div>
              </div>
            </Link>

            {/* Hamburger Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-[#0A0A0A] text-2xl w-10 h-10 flex items-center justify-center cursor-pointer relative z-50 ml-auto"
              aria-label="Menu"
            >
              <i className={`ri-${isMobileMenuOpen ? 'close' : 'menu'}-line`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed top-[64px] left-0 right-0 bottom-0 z-40 bg-[#0A0A0A]/95 overflow-y-auto">
          <div className="px-6 py-8">
            <div className="flex flex-col gap-4">
              {/* Navigation Items */}
              {navItems.map((item) => (
                item.isAnchor ? (
                  <a
                    key={item.path}
                    href={item.path}
                    onClick={handleCinewallOpMaatClick}
                    className="text-base py-2 transition-colors duration-300 text-[#BFBFBF] pl-4 cursor-pointer"
                  >
                    {item.label}
                  </a>
                ) : (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`text-base py-2 transition-colors duration-300 ${
                      isActive(item.path)
                        ? 'text-[#F3F1ED] font-semibold border-l-2 border-[#C7A86A] pl-4'
                        : 'text-[#BFBFBF] pl-4'
                    }`}
                  >
                    {item.label}
                  </Link>
                )
              ))}
              
              {/* CTA Buttons */}
              <Link
                to="/cinewall-configurator"
                onClick={() => setIsMobileMenuOpen(false)}
                className="bg-[#C7A86A] text-[#0F0F0F] px-6 py-3 rounded-md font-semibold text-base hover:bg-[#B89858] transition-colors duration-300 text-center mt-4 whitespace-nowrap"
              >
                Offerte aanvragen
              </Link>
              <Link
                to="/cinewall-showroom"
                onClick={() => setIsMobileMenuOpen(false)}
                className="border border-[#C7A86A] text-[#C7A86A] px-6 py-3 rounded-md font-semibold text-base hover:bg-[#C7A86A]/10 transition-colors duration-300 text-center whitespace-nowrap"
              >
                Plan showroomafspraak
              </Link>

              {/* Contact Info - Mobile Only */}
              <div className="border-t border-[#2A2A2A] mt-6 pt-6 space-y-4">
                <h3 className="text-[#F3F1ED] font-semibold text-sm uppercase tracking-wider mb-4">
                  Contact
                </h3>
                <a 
                  href="tel:+31624172293" 
                  className="flex items-center gap-3 text-[#BFBFBF] hover:text-[#C7A86A] transition-colors"
                >
                  <i className="ri-phone-line text-[#C7A86A] w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm">+31(0) 62 417 22 93</span>
                </a>
                <a 
                  href="tel:+31307810110" 
                  className="flex items-center gap-3 text-[#BFBFBF] hover:text-[#C7A86A] transition-colors"
                >
                  <i className="ri-phone-line text-[#C7A86A] w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm">+31(0) 30 781 01 10</span>
                </a>
                <a 
                  href="mailto:info@cinewallkopen.nl" 
                  className="flex items-center gap-3 text-[#BFBFBF] hover:text-[#C7A86A] transition-colors"
                >
                  <i className="ri-mail-line text-[#C7A86A] w-5 h-5 flex items-center justify-center"></i>
                  <span className="text-sm">info@cinewallkopen.nl</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
